import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { getAuth, initializeAuth, browserLocalPersistence, setPersistence, type Auth } from 'firebase/auth';
import {
  initializeFirestore,
  getFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import { getFunctions, type Functions } from 'firebase/functions';
import { Capacitor } from '@capacitor/core';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const isIOSNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let storage: FirebaseStorage;
let functions: Functions;

function initialize() {
  if (getApps().length === 0) {
    app = initializeApp(firebaseConfig);
  } else {
    app = getApps()[0];
  }

  if (isIOSNative) {
    // iOS: capacitor:// scheme causes IndexedDB issues with default persistence
    auth = initializeAuth(app, { persistence: browserLocalPersistence });
    // iOS: WKWebView CORS blocks WebChannel → force long polling
    db = initializeFirestore(app, { experimentalForceLongPolling: true });
  } else {
    auth = getAuth(app);
    setPersistence(auth, browserLocalPersistence).catch(() => {});
    // Multi-tab offline persistence (web + Android)
    try {
      db = initializeFirestore(app, {
        localCache: persistentLocalCache({
          tabManager: persistentMultipleTabManager(),
        }),
      });
    } catch {
      db = getFirestore(app);
    }
  }

  storage = getStorage(app);
  functions = getFunctions(app);
}

initialize();

export { app, auth, db, storage, functions, isIOSNative };
